import React, { useState } from 'react';
import SecurityShell, { cardStyle, pillStyle } from '../../components/security/SecurityShell';
import useSecurityFieldContext from '../../hooks/useSecurityFieldContext';
import { createIncident, getCurrentCoords } from '../../utils/securityFieldOps';

const INCIDENT_TYPES = ['general', 'trespassing', 'theft', 'damage', 'access_issue', 'suspicious_activity'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];

export default function SecurityIncidentsPage() {
  const { orgId, activeShift, siteId, recentIncidents } = useSecurityFieldContext();
  const [form, setForm] = useState({ title: '', type: 'general', severity: 'medium', notes: '' });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!orgId) return setMessage('No linked organization found.');
    setBusy(true);
    setMessage('');
    try {
      const coords = await getCurrentCoords().catch(() => null);
      const result = await createIncident({
        orgId,
        siteId: activeShift?.siteId || siteId,
        shiftId: activeShift?.id || '',
        payload: { ...form, lat: coords?.lat, lng: coords?.lng },
        files,
      });
      setMessage(`Incident saved. ${result.photoUrls?.length || 0} image(s) uploaded.`);
      setForm({ title: '', type: 'general', severity: 'medium', notes: '' });
      setFiles([]);
      const input = document.getElementById('incident-files');
      if (input) input.value = '';
    } catch (err) {
      setMessage(err?.message || 'Could not save incident.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SecurityShell
      title="Incident Reports"
      subtitle="Field teams can now log incidents with notes, severity, GPS metadata, and photo uploads from the device camera or gallery."
      aside={
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={cardStyle}>
            <div style={styles.sideKicker}>ACTIVE SHIFT</div>
            <div style={styles.sideMetric}>{activeShift ? 'Linked' : 'Not linked'}</div>
            <div style={styles.sideCopy}>{activeShift ? 'This incident will attach to the active shift.' : 'Incidents can still be saved without an active shift.'}</div>
          </div>
          <div style={cardStyle}>
            <div style={styles.sideKicker}>PHOTO READY</div>
            <div style={styles.sideMetric}>{files.length}</div>
            <div style={styles.sideCopy}>Selected image file(s).</div>
          </div>
        </div>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
        <div style={cardStyle}>
          <div style={styles.heading}>Incident Details</div>
          <div style={styles.grid}>
            <label style={styles.labelBlock}>
              <span>Title</span>
              <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Short incident title" style={styles.input} />
            </label>
            <label style={styles.labelBlock}>
              <span>Type</span>
              <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} style={styles.input}>
                {INCIDENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <label style={styles.labelBlock}>
              <span>Severity</span>
              <select value={form.severity} onChange={(e) => setForm((p) => ({ ...p, severity: e.target.value }))} style={styles.input}>
                {SEVERITIES.map((level) => <option key={level} value={level}>{level}</option>)}
              </select>
            </label>
          </div>
          <label style={{ ...styles.labelBlock, marginTop: 14 }}>
            <span>Notes</span>
            <textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Describe what happened, who was involved, and what action was taken." style={styles.textarea} />
          </label>
        </div>

        <div style={cardStyle}>
          <div style={styles.heading}>Photo Evidence</div>
          <div style={styles.copy}>Choose one or more images from the camera or gallery. Neo uploads them to Firebase Storage and stores the URLs on the incident record.</div>
          <input id="incident-files" type="file" accept="image/*" capture="environment" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} style={{ marginTop: 14 }} />
          <div style={styles.fileList}>
            {files.length ? files.map((file) => <span key={`${file.name}-${file.size}`} style={pillStyle}>{file.name}</span>) : <div style={styles.empty}>No images selected yet.</div>}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={styles.heading}>Recent incidents</div>
          <div style={styles.fileList}>
            {recentIncidents.length ? recentIncidents.map((row) => <span key={row.id} style={pillStyle}>{row.title || row.type || 'incident'} · {row.severity || 'medium'}</span>) : <div style={styles.empty}>No incidents saved yet.</div>}
          </div>
        </div>

        <div style={cardStyle}>
          <button type="submit" disabled={busy} style={styles.primaryBtn}>Save incident</button>
          {message ? <div style={styles.notice}>{message}</div> : null}
        </div>
      </form>
    </SecurityShell>
  );
}

const styles = {
  heading: { fontSize: 18, fontWeight: 900 },
  copy: { marginTop: 10, lineHeight: 1.7, opacity: .88 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 14 },
  labelBlock: { display: 'grid', gap: 8, fontWeight: 700 },
  input: { width: '100%', padding: '14px 16px', borderRadius: 16, background: 'rgba(255,255,255,.05)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,.1)' },
  textarea: { minHeight: 140, width: '100%', padding: '14px 16px', borderRadius: 16, background: 'rgba(255,255,255,.05)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,.1)', resize: 'vertical' },
  fileList: { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 },
  empty: { opacity: .8 },
  primaryBtn: { cursor: 'pointer', color: '#03121c', background: 'linear-gradient(90deg, #10b981 0%, #38bdf8 100%)', padding: '12px 15px', borderRadius: 14, border: 0, fontWeight: 900 },
  notice: { marginTop: 14, padding: 14, borderRadius: 16, background: 'rgba(56,189,248,.08)', border: '1px solid rgba(56,189,248,.18)', lineHeight: 1.6 },
  sideKicker: { fontSize: 12, fontWeight: 900, letterSpacing: '.08em', opacity: .7 },
  sideMetric: { marginTop: 10, fontSize: 24, fontWeight: 900 },
  sideCopy: { marginTop: 8, lineHeight: 1.6, opacity: .82 },
};
